import type { McpKeyRotateResponse, UserRole } from '../types/api'
import { apiClient } from './client'

export interface AdminSecondaryEmail {
  id: number
  email: string
  verified: boolean
}

export interface AdminOAuthIdentity {
  id: number
  provider: string
  providerEmail: string | null
}

export interface AdminUser {
  id: number
  email: string
  username: string
  role: UserRole
  isActive: boolean
  emailVerified?: boolean
  createdAt: string
  lastLoginAt?: string | null
  lastLoginMethod?: string | null
  lastLoginIdentifier?: string | null
  linkedMethods?: string[]
  secondaryEmails?: AdminSecondaryEmail[]
  oauthIdentities?: AdminOAuthIdentity[]
  /** True when isActive is false because this user signed up via OAuth and is awaiting admin approval. */
  pendingOAuthApproval?: boolean
  mcpKeyPrefix?: string | null
  mcpKeyLastUsedAt?: string | null
  /** Full MCP key — admin Users list only (encrypted at rest) */
  mcpKeyToken?: string | null
}

export interface CreateAdminUserResponse extends AdminUser {
  mcpKey: {
    token: string
    tokenPrefix: string
  }
}

export interface TransferAdminResponse {
  source: { id: number; role: string; isActive: boolean }
  target: { id: number; role: string; isActive: boolean }
}

export async function listAdminUsers(orgId?: number): Promise<AdminUser[]> {
  const params = orgId != null ? `?orgId=${orgId}` : ''
  return apiClient.get<AdminUser[]>(`/admin/users${params}`)
}

export async function createAdminUser(data: {
  email: string
  username: string
  password: string
  role?: UserRole
}): Promise<CreateAdminUserResponse> {
  return apiClient.post<CreateAdminUserResponse>('/admin/users', data)
}

export async function updateAdminUser(
  id: number,
  data: { role?: UserRole; isActive?: boolean }
): Promise<AdminUser> {
  return apiClient.patch<AdminUser>(`/admin/users/${id}`, data)
}

export async function rotateUserMcpKey(userId: number): Promise<McpKeyRotateResponse> {
  return apiClient.post<McpKeyRotateResponse>(`/admin/users/${userId}/mcp-keys/rotate`)
}

export async function resetUserPassword(
  userId: number,
  password: string
): Promise<{ message: string }> {
  return apiClient.post<{ message: string }>(`/admin/users/${userId}/reset-password`, {
    password,
  })
}

export async function addUserSecondaryEmail(
  userId: number,
  email: string
): Promise<AdminSecondaryEmail> {
  return apiClient.post<AdminSecondaryEmail>(`/admin/users/${userId}/emails`, { email })
}

export async function removeUserSecondaryEmail(userId: number, emailId: number): Promise<void> {
  await apiClient.delete(`/admin/users/${userId}/emails/${emailId}`)
}

export async function unlinkUserOAuthIdentity(userId: number, identityId: number): Promise<void> {
  await apiClient.delete(`/admin/users/${userId}/oauth-identities/${identityId}`)
}

export async function logoutAllUserSessions(userId: number): Promise<{ message: string }> {
  return apiClient.post<{ message: string }>(`/admin/users/${userId}/logout-all`)
}

export async function transferAdminRole(
  sourceUserId: number,
  data: {
    targetUserId: number
    sourceRole: 'project_manager' | 'user'
    disableSource?: boolean
  }
): Promise<TransferAdminResponse> {
  return apiClient.post<TransferAdminResponse>(`/admin/users/${sourceUserId}/transfer-admin`, data)
}

/** External IdP account password pages (documentation links only). */
export const IDP_PASSWORD_URLS: Record<string, { label: string; url: string }> = {
  google: {
    label: 'Change Google password',
    url: 'https://myaccount.google.com/signinoptions/password',
  },
  microsoft: {
    label: 'Change Microsoft password',
    url: 'https://account.live.com/password/Change',
  },
  github: {
    label: 'Change GitHub password',
    url: 'https://github.com/settings/security',
  },
}
